import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SkillStatus =
  | "off"
  | "shadow"
  | "on"
  | "deprecated";

export const SKILL_STATUSES: SkillStatus[] = [
  "off",
  "shadow",
  "on",
  "deprecated",
];

/** Allowed forward transitions (v20 simplified lifecycle). */
export const ALLOWED_TRANSITIONS: Record<SkillStatus, SkillStatus[]> = {
  off: ["shadow", "on", "deprecated"],
  shadow: ["on", "off", "deprecated"],
  on: ["off", "shadow", "deprecated"],
  deprecated: [],
};

export interface IntentSignature {
  primary_intent: string | null;
  secondary_intents: string[];
  excluded_intents: string[];
}

export interface PreCondition {
  key: string;
  op: "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "exists" | "not_exists";
  value?: unknown;
  description?: string;
}

export interface PostCondition {
  check: "contains" | "not_contains" | "matches" | "min_len" | "max_len" | "json_has";
  target: string;
  description?: string;
}

export interface ModelPreference {
  provider?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface SkillV19Row {
  id: string;
  slug: string;
  version: number;
  org_id: string | null;
  group_id: string | null;
  name: string;
  description: string;
  status: SkillStatus;
  intent_signature: IntentSignature;
  context_features: Record<string, unknown>;
  pre_conditions: PreCondition[];
  post_conditions: PostCondition[];
  prompt_template: string;
  tools_allowed: string[];
  model_preference: ModelPreference | null;
  refusal_policy: Record<string, unknown> | null;
  telemetry_config: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  published_by: string | null;
  published_at: string | null;
  deprecate_after: string | null;
  notes: string | null;
  updated_at: string;
}

export type SkillV19Input = Omit<
  SkillV19Row,
  "id" | "created_at" | "updated_at" | "published_at" | "published_by" | "version" | "deprecate_after"
> & {
  version?: number;
  deprecate_after?: string | null;
};

const TABLE = "syra_skills_v19" as const;

export function useSkillsV19() {
  return useQuery({
    queryKey: ["skills_v19"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLE)
        .select("*")
        .order("slug", { ascending: true })
        .order("version", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SkillV19Row[];
    },
  });
}

export function useCreateSkillV19() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SkillV19Input) => {
      const { data, error } = await supabase
        .from(TABLE)
        .insert([{ ...input, version: input.version ?? 1 }] as never)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as SkillV19Row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills_v19"] }),
  });
}

/**
 * Edits the editable fields of a skill. The DB trigger
 * `block_published_skill_v19_edit` will reject mutations to immutable fields
 * when status is `published`/`canary`.
 */
export function useUpdateSkillV19() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<SkillV19Input> }) => {
      const { data, error } = await supabase
        .from(TABLE)
        .update(patch as never)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as SkillV19Row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills_v19"] }),
  });
}

export function useTransitionSkillStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: SkillStatus }) => {
      const { data, error } = await supabase
        .from(TABLE)
        .update({ status } as never)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as SkillV19Row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills_v19"] }),
  });
}

/**
 * Forks a skill into a new version (immutability per V19 §4.4).
 * Copies all fields, increments version, resets status to `draft`.
 */
export function useForkSkillV19() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (source: SkillV19Row) => {
      const { data: existing, error: existingErr } = await supabase
        .from(TABLE)
        .select("version")
        .eq("slug", source.slug)
        .order("version", { ascending: false })
        .limit(1);
      if (existingErr) throw existingErr;
      const nextVersion = ((existing?.[0]?.version as number | undefined) ?? source.version) + 1;

      const payload = {
        slug: source.slug,
        version: nextVersion,
        org_id: source.org_id,
        group_id: source.group_id,
        name: source.name,
        description: source.description,
        status: "off" as SkillStatus,
        intent_signature: source.intent_signature,
        context_features: source.context_features,
        pre_conditions: source.pre_conditions,
        post_conditions: source.post_conditions,
        prompt_template: source.prompt_template,
        tools_allowed: source.tools_allowed,
        model_preference: source.model_preference,
        refusal_policy: source.refusal_policy,
        telemetry_config: source.telemetry_config,
        notes: source.notes,
      };
      const { data, error } = await supabase
        .from(TABLE)
        .insert([payload] as never)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as SkillV19Row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills_v19"] }),
  });
}
